// src/components/Admin/ItemManager/ItemUsageLog.jsx
import { useEffect, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Paper, Button,
  Stack, TextField, Typography, Pagination,
} from '@mui/material';
import api from '../../../api/axios';
import ConfirmMemoModal from './ConfirmMemoModal';

export default function ItemUsageLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  /* ───── 데이터 로드 ───── */
  const fetchLogs = () => {
    api.get('/api/rewards/item-admin/pending/')
      .then((res) => setLogs(res.data.pending || []))
      .catch(() => alert('대기 쿠폰을 불러오는 데 실패했습니다.'))
      .finally(() => setLoading(false));
  };
  useEffect(fetchLogs, []);

  /* ───── 필터링 ───── */
  const filtered = logs.filter(
    (l) =>
      (!selectedDate || l.used_at?.startsWith(selectedDate)) &&
      (l.username.includes(search) || l.item_name.includes(search)),
  );
  const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1;
  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  /* ───── 처리 핸들러 ───── */
  const handleApprove = (log) => {
    document.activeElement?.blur();
    setSelectedLog(log);
    setIsEditing(false);
    setModalOpen(true);
  };
  const handleRefund = (log) => {
    if (!window.confirm('이 쿠폰을 환불하시겠습니까?')) return;
    api.post(`/api/rewards/item-admin/confirm/${log.id}/`, { action: 'refund', memo: '' })
      .then((res) => { alert(res.data.message); fetchLogs(); })
      .catch(() => alert('환불 처리에 실패했습니다.'));
  };
  const handleSubmitMemo = (memo) => {
    const method = isEditing ? 'patch' : 'post';
    api[method](`/api/rewards/item-admin/confirm/${selectedLog.id}/`, {
      action: 'approve',
      memo,
    })
      .then((res) => { alert(res.data.message); setModalOpen(false); fetchLogs(); })
      .catch(() => alert('승인 처리에 실패했습니다.'));
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  /* ───── Render ───── */
  return (
    <Box
      sx={{
        /* 모바일: 90 % 축소 */
        '@media (max-width:600px)': {
          transform: 'scale(0.9)',
          transformOrigin: 'top left',
        },
      }}
    >
      {/* 검색 바 */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        alignItems="center"
      >
        <TextField
          size="small"
          label="검색 (닉네임/아이템명)"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <TextField
          type="date"
          size="small"
          label="날짜 선택"
          InputLabelProps={{ shrink: true }}
          value={selectedDate}
          onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
        />
        <Typography variant="body2">총 {filtered.length}건</Typography>
      </Stack>

      {filtered.length === 0 ? (
        <Typography>현재 승인 대기 중인 쿠폰이 없습니다.</Typography>
      ) : (
        <>
          {/* 테이블 */}
          <Paper
            sx={{
              '@media (max-width:600px)': {
                width: 'max-content',
                overflowX: 'auto',
              },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>유저명</TableCell>
                  <TableCell>쿠폰명</TableCell>
                  <TableCell>사용일시</TableCell>
                  <TableCell>처리</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.username}</TableCell>
                    <TableCell>{log.item_name}</TableCell>
                    <TableCell>{log.used_at}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleApprove(log)}
                        >
                          승인
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => handleRefund(log)}
                        >
                          환불
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                showFirstButton
                showLastButton
                sx={{ '@media (max-width:600px)': { transform: 'scale(0.9)' } }}
              />
            </Box>
          )}
        </>
      )}

      {/* 메모 모달 */}
      <ConfirmMemoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitMemo}
        initialMemo={selectedLog?.confirmed_memo || ''}
        isEditing={isEditing}
      />
    </Box>
  );
}
